export type ReportTypeMasterItem = {
  id?: number | string | null;
  label?: string | null;
  value?: string | null;
  color?: string | null;
  attr_id?: number | null;
  department_id?: number | null;
  team_id?: number | null;
  order_number?: number | null;
};

const toNumOrNull = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isTeamCommon = (v: unknown): boolean => {
  const n = toNumOrNull(v);
  return n == null || n === 0;
};

const isDepartmentCommon = (v: unknown): boolean => {
  const n = toNumOrNull(v);
  return n == null || n === 0;
};

const sortByOrder = (a: ReportTypeMasterItem, b: ReportTypeMasterItem): number => {
  const ao = Number(a.order_number ?? Number.MAX_SAFE_INTEGER);
  const bo = Number(b.order_number ?? Number.MAX_SAFE_INTEGER);
  if (ao !== bo) return ao - bo;
  const ai = Number(a.id ?? Number.MAX_SAFE_INTEGER);
  const bi = Number(b.id ?? Number.MAX_SAFE_INTEGER);
  if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) return ai - bi;
  return String(a.label ?? a.value ?? '').localeCompare(String(b.label ?? b.value ?? ''), 'ja');
};

const dedupeByValueOrLabel = (items: ReportTypeMasterItem[]): ReportTypeMasterItem[] => {
  const seen = new Set<string>();
  const out: ReportTypeMasterItem[] = [];
  for (const it of items) {
    const value = String(it.value ?? '').trim();
    const label = String(it.label ?? '').trim();
    const key = value !== '' ? `v:${value}` : `l:${label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(it);
  }
  return out;
};

export const filterReportTypesForOrg = (
  reportTypes: ReportTypeMasterItem[] | null | undefined,
  departmentId?: number | null,
  teamId?: number | null,
): ReportTypeMasterItem[] => {
  const list = Array.isArray(reportTypes) ? reportTypes : [];
  const dep = toNumOrNull(departmentId);
  const team = toNumOrNull(teamId);
  if (dep == null) return [];

  const byPriority: Array<(item: ReportTypeMasterItem) => boolean> = [];
  if (team != null) {
    // Priority 1: department_id === departmentId && team_id === teamId
    byPriority.push((item) =>
      toNumOrNull(item.department_id) === dep &&
      toNumOrNull(item.team_id) === team,
    );
  }
  // Priority 2: department_id === departmentId && (team_id === 0 || team_id == null)
  byPriority.push((item) =>
    toNumOrNull(item.department_id) === dep &&
    isTeamCommon(item.team_id),
  );
  // Priority 3: (department_id === 0 || department_id == null) && (team_id === 0 || team_id == null)
  byPriority.push((item) =>
    isDepartmentCommon(item.department_id) &&
    isTeamCommon(item.team_id),
  );

  let candidates: ReportTypeMasterItem[] = [];
  for (const pick of byPriority) {
    candidates = list.filter(pick);
    if (candidates.length > 0) break;
  }

  return dedupeByValueOrLabel([...candidates].sort(sortByOrder));
};

export const resolveReportTypeAttrId = (
  reportTypes: ReportTypeMasterItem[] | null | undefined,
): number | null => {
  const list = Array.isArray(reportTypes) ? reportTypes : [];
  for (const item of list) {
    const id = Number(item.attr_id);
    if (Number.isFinite(id)) return id;
  }
  return null;
};
